import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types/Select';
import { validateDateThatIsEqualOrAfter } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { getCurrentDateISO } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { updateCartName } from 'api/operations/cartServiceV2';
import { updateInitiateFormAnswers, updateProposalName } from 'api/operations/enrollmentService';
import { updateGroup } from 'api/operations/groupService';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import CartModule from 'store/modules/cart/cartModule';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import GroupModule from 'store/modules/group/groupModule';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { bpHistory } from 'global/bpHistory';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';

export interface RFQInitiationDomainParams {
  periodId: string;
  cartId: string;
}

type RFQInitiationFormFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  responseDueDate: FormControl<string>;
};

export default class RFQInitiationPageStore {
  private readonly _domainParams: RFQInitiationDomainParams;
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private _cartModule = new CartModule();
  private _groupModule = new GroupModule();
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _isInitialDataLoading = false;
  private _isActionLoading = false;

  public proposalInfoForm: FormController<RFQInitiationFormFields>;

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  public get canCompleteStep(): boolean {
    return (
      this.proposalInfoForm.basicFieldsValidationsPassed &&
      !this.proposalInfoForm.isLoading &&
      !this._isActionLoading &&
      !this.isInitialDataLoading
    );
  }

  private _setInitialDataLoading(isLoading: boolean): void {
    this._isInitialDataLoading = isLoading;
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    const { cartId, periodId } = this._domainParams;

    this._setInitialDataLoading(true);

    await this._cartModule.getCart(cartId);

    await Promise.all([
      this._groupModule.getGroup(this._cartModule.cart.groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId),
    ]);

    runInAction(() => {
      const { effectiveDate, zipCode, state, county } = this._cartModule.lastUpdateBrowsingCriteria;
      const proposal = this._enrollmentPeriodModule.getProposalById(cartId);

      this.proposalInfoForm.populateFields({
        proposalName: this._cartModule.cart.cartName,
        effectiveDate,
        zip: zipCode,
        state,
        county,
        responseDueDate: proposal?.initiateFormAnswers?.responseDueDate ?? '',
      });

      this._setInitialDataLoading(false);
    });
  }

  public async saveProposal(): Promise<boolean> {
    const { cartId, periodId } = this._domainParams;

    this._setActionLoading(true);

    const { proposalName, responseDueDate } = this.proposalInfoForm.getFormDataObject() as Record<string, string>;
    const inputGroup = buildInputGroupFromGroup(this._groupModule.group);
    const proposal = this._enrollmentPeriodModule.getProposalById(cartId);

    try {
      if (
        this._cartModule.cart.cartName !== proposalName ||
        proposal?.initiateFormAnswers?.responseDueDate !== responseDueDate
      ) {
        await Promise.all([
          updateCartName(cartId, proposalName),
          updateProposalName(periodId, cartId, proposalName),
          updateInitiateFormAnswers(periodId, cartId, {
            ...proposal?.initiateFormAnswers,
            responseDueDate,
          }),
          updateGroup(inputGroup),
        ]);
      }

      runInAction(() => {
        this._setActionLoading(false);
        this.redirectToChecklist();
      });

      return true;
    } catch (error) {
      this._setActionLoading(false);
      toastManager.addToast('An error occurred while updating the proposal answers.', ToastColorTypes.WARNING);
      return false;
    }
  }

  public redirectToChecklist(): void {
    const { periodId, cartId } = this._domainParams;
    bpHistory.push(getRFQProposalChecklistURL({ periodId, cartId }));
  }

  constructor(params: RFQInitiationDomainParams) {
    this._domainParams = params;

    this.proposalInfoForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
        responseDueDate: new FormControl<string>('', {
          optional: false,
          customValidations: [
            {
              // Validation ensures the selected date is equal to or later than the current day.
              check: (value: string): boolean => validateDateThatIsEqualOrAfter(value, getCurrentDateISO()),
              msg: 'The date cannot be in the past',
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
        ],
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQInitiationPageStore({ periodId: '', cartId: '' }));

export const useRFQInitiationPageStore = (): RFQInitiationPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
